import { cva, type VariantProps } from 'class-variance-authority';

import { cn, withColor } from '../../../lib/stylingUtils';
import { allColors, type ThemeColor } from '../../../types/theme';

const hashString = (s: string) =>
  [...s].reduce((acc, c) => c.charCodeAt(0) + ((acc << 5) - acc), 0);

const getInitials = (name: string) => {
  const parts = name.trim().split(/\s+/);
  if (parts.length > 1) {
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  }
  return (name[0] ?? '').toUpperCase();
};

const getColorFromName = (name: string): ThemeColor =>
  allColors[Math.abs(hashString(name)) % allColors.length];

const avatarOuter = cva('@Avatar bg-white', {
  variants: {
    size: {
      sm: 'size-6 text-sm',
      md: 'size-8 text-md',
      lg: 'size-10 text-lg',
      xl: 'size-12 text-base',
    },
    variant: {
      default: 'rounded-full',
      profile: 'h-20 min-w-14 rounded-sm',
      square: 'rounded-sm',
    },
  },
  defaultVariants: {
    size: 'md',
    variant: 'default',
  },
});

const avatarInner = cva(
  'flex size-full items-center justify-center overflow-hidden border font-medium',
  {
    variants: {
      variant: {
        default: 'rounded-full',
        profile: 'rounded-sm text-xl',
        square: 'rounded-sm',
      },
      colorSource: {
        theme: 'border-custom-color/20 bg-custom-color/10 text-custom-color',
        image: 'border-neutral/20 bg-neutral/10',
      },
    },
    defaultVariants: {
      variant: 'default',
      colorSource: 'theme',
    },
  },
);

type AvatarVariants = VariantProps<typeof avatarOuter>;

export interface AvatarProps {
  name: string;
  size?: AvatarVariants['size'];
  variant?: AvatarVariants['variant'];
  className?: string;
  imageUrl?: string;
  color?: ThemeColor;
}

//
// ⚛️ ⚛️ ⚛️ AVATAR ⚛️ ⚛️ ⚛️
//
export const Avatar = ({
  name,
  size = 'md',
  variant = 'default',
  className,
  imageUrl,
  color,
}: AvatarProps) => {
  const initials = getInitials(name);
  const resolvedColor = color ?? getColorFromName(name);

  return (
    <div
      className={cn('@Avatar', avatarOuter({ size, variant }), className)}
      style={imageUrl ? undefined : withColor(resolvedColor)}
    >
      <div
        className={avatarInner({
          variant,
          colorSource: imageUrl ? 'image' : 'theme',
        })}
      >
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={name}
            className="h-full w-full object-cover"
          />
        ) : (
          initials
        )}
      </div>
    </div>
  );
};
